import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { blogPosts, blogCategories } from "@/mocks/blog";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

export default function BlogPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const featured = blogPosts.filter((p) => p.featured);
  const filtered = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesCategory =
        activeCategory === "All" || post.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        post.tags.some((t) => t.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const nonFeaturedFiltered = filtered.filter((p) => !p.featured || activeCategory !== "All" || searchQuery);

  const handlePostClick = (slug: string) => {
    navigate(`/blog/${slug}`);
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Navbar onRequestLeads={() => {}} />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-[#f9fafb]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#16a34a] uppercase tracking-widest mb-4">
              <i className="ri-article-line"></i> Solar Industry Resources
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
              Insights for Solar<br />
              <span className="text-[#16a34a]">Installers Who Close</span>
            </h1>
            <p className="text-gray-500 text-lg leading-relaxed">
              Sales playbooks, market data, and lead generation strategies from the team behind 1,200+ sold leads.
            </p>
          </div>

          {/* Search */}
          <div className="mt-8 max-w-md">
            <div className="relative">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === cat
                    ? "bg-[#16a34a] text-white"
                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Featured Posts — only show when no filter/search active */}
        {activeCategory === "All" && !searchQuery && featured.length > 0 && (
          <section className="mb-14">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">Featured</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featured.map((post, i) => (
                <article
                  key={post.slug}
                  onClick={() => handlePostClick(post.slug)}
                  className={`group cursor-pointer rounded-2xl overflow-hidden border border-gray-100 hover:border-[#16a34a]/30 transition-all duration-300 ${
                    i === 0 ? "lg:col-span-1" : ""
                  }`}
                >
                  <div className="relative overflow-hidden h-52">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <span className="absolute top-3 left-3 bg-[#16a34a] text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 leading-snug mb-2 group-hover:text-[#16a34a] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 ${post.authorColor} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                          {post.authorInitials}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-700">{post.author}</p>
                          <p className="text-xs text-gray-400">{post.publishedAt}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-time-line"></i> {post.readTime}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* All / Filtered Posts */}
        <section>
          {activeCategory === "All" && !searchQuery ? (
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">All Articles</h2>
          ) : (
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                {filtered.length} {filtered.length === 1 ? "Article" : "Articles"}
                {activeCategory !== "All" ? ` in ${activeCategory}` : ""}
                {searchQuery ? ` matching "${searchQuery}"` : ""}
              </h2>
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-gray-400 text-xl"></i>
              </div>
              <p className="text-gray-500 font-medium">No articles found</p>
              <p className="text-gray-400 text-sm mt-1">Try a different search term or category</p>
              <button
                onClick={() => { setSearchQuery(""); setActiveCategory("All"); }}
                className="mt-4 text-sm text-[#16a34a] font-medium hover:underline cursor-pointer"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(activeCategory === "All" && !searchQuery ? nonFeaturedFiltered : filtered).map((post) => (
                <article
                  key={post.slug}
                  onClick={() => handlePostClick(post.slug)}
                  className="group cursor-pointer rounded-2xl overflow-hidden border border-gray-100 hover:border-[#16a34a]/30 transition-all duration-300"
                >
                  <div className="relative overflow-hidden h-44">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-gray-900 leading-snug mb-2 group-hover:text-[#16a34a] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 ${post.authorColor} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                          {post.authorInitials}
                        </div>
                        <p className="text-xs text-gray-500">{post.author}</p>
                      </div>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-time-line"></i> {post.readTime}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Newsletter CTA */}
        <section className="mt-20 bg-[#f0fdf4] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">Get new articles in your inbox</h3>
            <p className="text-gray-500 text-sm">Solar sales tips, market updates, and lead buying strategies — weekly.</p>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <input
              type="email"
              placeholder="your@email.com"
              className="flex-1 md:w-64 px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] bg-white"
            />
            <button className="bg-[#16a34a] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-[#15803d] transition-colors cursor-pointer whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
